import type { CrClassification, CrPriority, CrStatus } from "@shared/types/supabase.types";
import { CheckCheck, Hammer, Lightbulb, ListTodo, PencilLine } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { CrSortDirection, CrSortField } from "./cr-sorting";

/** CR type (classification) order (CR-118) — the `cr_classification` enum
 * order, also the order the new-CR dialog offers them. */
export const CR_CLASSIFICATIONS: CrClassification[] = [
  "feature",
  "improvement",
  "fix",
  "technical",
];

export const CR_CLASSIFICATION_CLASS: Record<CrClassification, string> = {
  feature: "bg-blue-100 text-blue-800 border-transparent dark:bg-blue-900/30 dark:text-blue-400",
  improvement:
    "bg-amber-100 text-amber-800 border-transparent dark:bg-amber-900/30 dark:text-amber-400",
  fix: "bg-red-100 text-red-800 border-transparent dark:bg-red-900/30 dark:text-red-400",
  technical: "bg-secondary text-secondary-foreground border-transparent",
};

/** The user-facing name of a CR type (classification) for the board's type
 * filter (CR-118) and the new-CR dialog (CR-131). A switch, not a `Record`,
 * so wuchale extracts the labels (see `getCrStatusLabel`); an unknown value
 * degrades to itself. */
export function getCrClassificationLabel(classification: CrClassification): string {
  switch (classification) {
    case "feature":
      return "Feature";
    case "improvement":
      return "Improvement";
    case "fix":
      return "Fix";
    case "technical":
      return "Technical";
    default: {
      const unknownClassification: never = classification;
      return unknownClassification;
    }
  }
}

/** The one-line explanation the new-CR dialog shows next to each CR type
 * (CR-131). A switch, not a `Record`, so wuchale extracts the descriptions
 * (see `getCrStatusLabel`); an unknown value degrades to itself. */
export function getCrClassificationDescription(classification: CrClassification): string {
  switch (classification) {
    case "feature":
      return "New functionality that did not exist before";
    case "improvement":
      return "Enhancement to existing functionality";
    case "fix":
      return "Correction of a bug or unintended behaviour";
    case "technical":
      return "Refactor, infrastructure, or non-user-facing work";
    default: {
      const unknownClassification: never = classification;
      return unknownClassification;
    }
  }
}

/** CR status order, labels and icons — single source shared by the board and
 * the detail page. */
export const CR_STATUSES: CrStatus[] = ["suggested", "defining", "planning", "building", "done"];

/**
 * CR-183: `suggested` is only ever written by a contributor's suggestion (a DB
 * trigger refuses any other path), so no picker offers it and a board shows
 * its column only while it holds a CR (`hasSuggested`).
 */
export function visibleCrStatuses(hasSuggested: boolean): CrStatus[] {
  return hasSuggested ? CR_STATUSES : CR_STATUSES.filter((status) => status !== "suggested");
}

/**
 * The user-facing name of a CR status. CR-103 renamed the `planning` label to
 * "Ready"; the stored value is untouched — the DB CHECK, the MCP
 * `update_cr_status` enum, the integration API and the skills all still speak
 * `planning`, and this switch is the only place the two meet.
 *
 * It is a function rather than the `Record` it used to be because wuchale's
 * jsx adapter drops every script-scope string it finds in a variable
 * initializer (`createJsxHeuristic`, `declaring === 'variable'`), so a
 * `Record` literal is never extracted and these four labels were not
 * translated at all — fr/de read "Planning". Returned from a function body
 * they are extracted, and a lowercase, non-`use` name gets wuchale's plain
 * runtime rather than the React-hook one, which keeps the helper safe to call
 * from `.map()` callbacks and `switch` arms (same shape as
 * `getCreditStatusLabel`).
 *
 * A value outside the union — jsonb event metadata, a row written by a newer
 * server — degrades to itself rather than rendering nothing.
 */
export function getCrStatusLabel(status: CrStatus): string {
  switch (status) {
    case "suggested":
      return "Suggested";
    case "defining":
      return "Defining";
    case "planning":
      return "Ready";
    case "building":
      return "Building";
    case "done":
      return "Done";
    default: {
      const unknownStatus: never = status;
      return unknownStatus;
    }
  }
}

export const CR_STATUS_ICON: Record<CrStatus, LucideIcon> = {
  suggested: Lightbulb,
  defining: PencilLine,
  planning: ListTodo,
  building: Hammer,
  done: CheckCheck,
};

/** CR priority picker/filter order (CR-44) — Linear convention: "none" first,
 * then most to least urgent. Icons live in `CrPriorityIcon`. */
export const CR_PRIORITIES: CrPriority[] = ["none", "urgent", "high", "medium", "low"];

/** The user-facing name of a CR priority — the new-CR dialog, the board's
 * priority filter, the card tooltip, the detail badge and picker, and the
 * activity feed all read it (CR-131). A switch, not the `Record` it used to
 * be, so wuchale extracts the five labels (see `getCrStatusLabel`); an unknown
 * value — jsonb event metadata, a row written by a newer server — degrades to
 * itself. */
export function getCrPriorityLabel(priority: CrPriority): string {
  switch (priority) {
    case "none":
      return "No priority";
    case "urgent":
      return "Urgent";
    case "high":
      return "High";
    case "medium":
      return "Medium";
    case "low":
      return "Low";
    default: {
      const unknownPriority: never = priority;
      return unknownPriority;
    }
  }
}

/** The name of a CR board sort field (CR-118). A switch, not a `Record`, so
 * wuchale extracts the labels (see `getCrStatusLabel`). */
export function getCrSortFieldLabel(field: CrSortField): string {
  switch (field) {
    case "updated":
      return "Last updated";
    case "sequential_id":
      return "CR number";
    case "priority":
      return "Priority";
    default: {
      const unknownField: never = field;
      return unknownField;
    }
  }
}

/** The direction words for a sort field (CR-118): dates read newest/oldest,
 * numbers highest/lowest, priorities most/least urgent. */
export function getCrSortDirectionLabel(field: CrSortField, direction: CrSortDirection): string {
  const desc = direction === "desc";
  switch (field) {
    case "updated":
      return desc ? "Newest first" : "Oldest first";
    case "sequential_id":
      return desc ? "Highest first" : "Lowest first";
    case "priority":
      return desc ? "Most urgent first" : "Least urgent first";
    default: {
      const unknownField: never = field;
      return unknownField;
    }
  }
}

/** The action row that closes the CR page's Chats and Linked documents lists
 * (CR-89, CR-84): a real button that reads as one more entry of the list it
 * ends. Shared so the two rows cannot drift apart — they sit on the same
 * screen, and a difference in either would show. */
export const CR_LIST_ACTION_ROW_CLASS =
  "text-primary focus-visible:ring-ring/50 inline-flex cursor-pointer items-center gap-2 rounded-sm text-sm outline-none hover:underline focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50";
